import { AxiosResponse } from 'axios';
import {
  TaskAssignmentResult,
  TaskAssignmentRoutesResult
} from 'types/interfaces/TaskAssignmentResult';
import { TaskExportSettings } from 'types/interfaces/TaskExportSettings';

import { api } from '../../http';
import {
  ActualOperation,
  PageRequest,
  Task,
  TaskCoordinate,
  TaskHistory,
  TaskStatusCoordinateDetails,
  TaskTransition,
  TreeNode
} from '../../types/interfaces';
import { PageResponse } from '../../types/interfaces';

import { ApiResponse } from './interfaces';

export const TaskService = {
  createTask(params: Task): Promise<AxiosResponse<ApiResponse<Task>>> {
    return api.post<ApiResponse<Task>>('task', params);
  },

  getTasks(
    params: PageRequest & {
      SortBy?: string;
      SortDirection?: string;
      ParsedFilters?: any[];
      ForSelection?: boolean;
      withoutEmployees?: boolean;
      withoutCurrentEmployee?: boolean;
      coordinate?: boolean;
    }
  ): Promise<AxiosResponse<PageResponse<Task> | undefined>> {
    const searchParams = new URLSearchParams();

    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined) {
        if (key === 'ParsedFilters') {
          searchParams.append(key, JSON.stringify(value));
        } else {
          searchParams.append(key, value.toString());
        }
      }
    });

    return api
      .get<ApiResponse<PageResponse<Task>>>('task', { params: searchParams })
      .then((resp) =>
        resp.data.isSuccess
          ? { ...resp, data: resp.data.value }
          : { ...resp, data: undefined }
      );
  },

  getDepartmentAssignment(
    params: PageRequest
  ): Promise<AxiosResponse<ApiResponse<PageResponse<Task>>>> {
    const searchParams = new URLSearchParams();
    Object.keys(params).forEach((key) => {
      if (params[key]) {
        searchParams.append(key, String(params[key]));
      }
    });

    return api.get<ApiResponse<PageResponse<Task>>>(
      'task?departmentAssignment=true',
      {
        params: searchParams
      }
    );
  },

  getDepartmentAssignmentSchedule(): Promise<AxiosResponse<ApiResponse<any>>> {
    return api.get<ApiResponse<any>>('task/department/assignment/schedule');
  },

  updateTaskDepartmentAssignment(
    taskId: number | string,
    departmentId: number | string
  ): Promise<AxiosResponse<ApiResponse<void>>> {
    return api.post<ApiResponse<void>>(
      `task/${taskId}/department/${departmentId}`
    );
  },

  AutoTaskAssignment(
    taskIds: number[],
    validationOnly: boolean
  ): Promise<AxiosResponse<ApiResponse<void>>> {
    return api.post<ApiResponse<void>>(`task/department`, {
      taskIds,
      validationOnly
    });
  },

  getPersonAssignment(
    params: PageRequest
  ): Promise<AxiosResponse<ApiResponse<PageResponse<Task>>>> {
    const searchParams = new URLSearchParams();
    Object.keys(params).forEach((key) => {
      if (params[key]) {
        searchParams.append(key, String(params[key]));
      }
    });
    return api.get<ApiResponse<PageResponse<Task>>>(
      'task?personAssignment=true',
      { params: searchParams }
    );
  },

  AutoTaskEmployeeAssignment(
    taskIds: number[],
    validationOnly: boolean
  ): Promise<AxiosResponse<ApiResponse<TaskAssignmentResult[]>>> {
    return api.post<ApiResponse<TaskAssignmentResult[]>>('task/employee', {
      taskIds,
      validationOnly
    });
  },

  AutoTaskEmployeeAssignmentByRoutes(params: {
    taskIds: number[];
    validationOnly: boolean;
    departmentId: number;
    date: string;
  }): Promise<AxiosResponse<ApiResponse<TaskAssignmentRoutesResult>>> {
    return api.post<ApiResponse<TaskAssignmentRoutesResult>>(
      'task/employee/optimization/routes',
      params
    );
  },

  getAutoAssignmentInfo(
    id: number
  ): Promise<AxiosResponse<ApiResponse<number>>> {
    return api.get<ApiResponse<number>>(`task/${id}/optimization/routes`);
  },

  getRequiredEmployeeCount(
    id: number | string
  ): Promise<AxiosResponse<ApiResponse<number>>> {
    return api.get<ApiResponse<number>>(`task/${id}/employee/required`);
  },

  updateTaskEmployees(
    id: number | string,
    employeeIds: number[]
  ): Promise<AxiosResponse<ApiResponse<void>>> {
    return api.post<ApiResponse<void>>(`task/${id}/employee`, employeeIds);
  },

  getTaskCoordinates(
    params: PageRequest
  ): Promise<AxiosResponse<TaskCoordinate[] | undefined>> {
    return api
      .get<TaskCoordinate[]>('task', {
        params: { ...params, coordinate: true }
      })
      .then((resp) => {
        return {
          ...resp,
          data: resp.status === 200 ? resp.data || [] : undefined
        };
      });
  },

  readTaskById(id: number): Promise<AxiosResponse<Task | undefined>> {
    return api
      .get<ApiResponse<Task>>(`task/${id}`)
      .then((resp) =>
        resp.data.isSuccess
          ? { ...resp, data: resp.data.value }
          : { ...resp, data: undefined }
      );
  },

  getTaskStatusCoordinates(
    id: number
  ): Promise<AxiosResponse<TaskStatusCoordinateDetails | undefined>> {
    return api
      .get<ApiResponse<TaskStatusCoordinateDetails>>(`task/${id}`, {
        params: { statusCoordinates: true }
      })
      .then((resp) =>
        resp.data.isSuccess
          ? { ...resp, data: resp.data.value }
          : { ...resp, data: undefined }
      );
  },

  updateTask(model: Task): Promise<AxiosResponse> {
    const newModel = {
      ...model,
      actualOperations: model.actualOperations.map((x: ActualOperation) => ({
        operation: x.operation,
        comment: x.comment,
        employees: x.employees,
        id: x.id,
        volume:
          !x.volume || (typeof x.volume === 'string' && x.volume.trim() === '')
            ? null
            : x.volume,
        measure: x.measure
      }))
    };
    return api.put<ApiResponse<void>>('task/update', newModel);
  },

  deleteTask(id: string): Promise<AxiosResponse> {
    return api.delete('task/' + id);
  },

  doTaskTransition(
    params: TaskTransition
  ): Promise<AxiosResponse<ApiResponse<void>>> {
    return api.post<ApiResponse<void>>(`task/${params.taskId}/status`, params);
  },

  getTaskHistory(
    id: number
  ): Promise<AxiosResponse<ApiResponse<Record<string, TaskHistory>>>> {
    return api.get<ApiResponse<Record<string, TaskHistory>>>(
      `task/${id}/history/`
    );
  },
  exportTaskHistoryExcel(id: number | string): Promise<AxiosResponse<Blob>> {
    return api.get(`task/${id}/history/export`, {
      responseType: 'blob'
    });
  },
  exportTasksHistoryExcel(params: PageRequest): Promise<AxiosResponse<Blob>> {
    const searchParams = new URLSearchParams();
    Object.keys(params).forEach((key) => {
      if (params[key]) {
        searchParams.append(key, params[key]!.toString());
      }
    });
    return api.get('task/history/export?' + new URLSearchParams(searchParams), {
      responseType: 'blob'
    });
  },
  getTaskTypes(): Promise<AxiosResponse<TreeNode[]>> {
    return api.get('task/type');
  },
  getTaskStatuses(): Promise<AxiosResponse<TreeNode[]>> {
    return api.get('task/status');
  },
  // TODO апи для работ(operation) начало
  getOperationListForSelection(
    withAttainments?: boolean
  ): Promise<AxiosResponse<TreeNode[]>> {
    return api.get('operation/getForSelection', {
      params: withAttainments ? { withAttainments: true } : undefined
    });
  },

  // TODO конец апи работ(operation)
  exportTasksExcel(params: PageRequest): Promise<AxiosResponse<Blob>> {
    const searchParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined) searchParams.append(key, value.toString());
    });

    return api.get('task/export', {
      params: searchParams,
      responseType: 'blob'
    });
  },

  getTasksTemplateExcel(): Promise<AxiosResponse<Blob>> {
    return api.get('task/export/template', {
      responseType: 'blob'
    });
  },

  exportWorksExcel(
    params: PageRequest & Partial<TaskExportSettings>
  ): Promise<AxiosResponse<Blob>> {
    const searchParams = new URLSearchParams();
    // Add the existing params to the searchParams
    Object.keys(params).forEach((key) => {
      if (params[key] !== undefined) {
        // Allow false values to be included
        searchParams.append(key, params[key]!.toString());
      }
    });
    return api.get(`operation/exportExcel?${searchParams.toString()}`, {
      responseType: 'blob'
    });
  },

  customExportExcel(
    params: PageRequest & Partial<TaskExportSettings>
  ): Promise<AxiosResponse<Blob>> {
    const searchParams = new URLSearchParams();
    // Add the existing params to the searchParams
    Object.keys(params).forEach((key) => {
      if (params[key] !== undefined) {
        // Allow false values to be included
        searchParams.append(key, params[key]!.toString());
      }
    });
    return api.get(`operation/customExportExcel?${searchParams.toString()}`, {
      responseType: 'blob'
    });
  },

  getTaskComments(
    id: number
  ): Promise<AxiosResponse<ApiResponse<Record<string, any>>>> {
    return api.get<ApiResponse<Record<string, any>>>(`TaskComment/task/${id}`);
  },

  exportTaskComments(id: number): Promise<AxiosResponse<Blob>> {
    return api.get(`TaskComment/task/${id}/export`, {
      responseType: 'blob'
    });
  },

  markCommentsAsRead(commentIds: number[]): Promise<AxiosResponse<void>> {
    const queryString = commentIds.map((id) => `commentIds=${id}`).join('&');
    return api.post<void>(`TaskComment/read?${queryString}`);
  },

  import(
    formData: FormData,
    validation = false
  ): Promise<AxiosResponse<{ tasks: Task[]; errors: string[] }>> {
    return api.post<{ tasks: Task[]; errors: string[] }>(
      'task/import',
      formData,
      {
        params: { validation },
        headers: { 'content-type': 'multipart/form-data' }
      }
    );
  },

  deleteTaskComment(id: number): Promise<AxiosResponse<void>> {
    return api.delete<void>(`TaskComment/${id}`);
  },

  updateTaskComment(
    id: number,
    text: string,
    taskId: number,
    device: string,
    files?: File[],
    deletedFileIds?: number[]
  ): Promise<AxiosResponse<ApiResponse<any>>> {
    const formData = new FormData();
    formData.append('Text', text);
    formData.append('TaskId', taskId.toString());
    formData.append('Device', device);

    deletedFileIds?.forEach((id) =>
      formData.append('DeletedFileIds', id.toString())
    );
    files?.forEach((file) => formData.append('Files', file));

    return api.put<ApiResponse<any>>(`TaskComment/${id}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },

  addTaskComment(
    text: string,
    taskId: number,
    device: string,
    files?: File[]
  ): Promise<AxiosResponse<ApiResponse<any>>> {
    const formData = new FormData();

    formData.append('Text', text);
    formData.append('TaskId', taskId.toString());
    formData.append('Device', device);

    if (files && files.length > 0) {
      files.forEach((file) => {
        formData.append('Files', file);
      });
    }

    return api.post<ApiResponse<any>>('TaskComment', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  }
};
